/**
 * The client project's PRODUCT version — what the running API reports as "Atlas Hub 1.4.2" in the
 * platform's user menu and environment banner (SmartStack reads the entry assembly's
 * AssemblyInformationalVersion).
 *
 * Single source of truth: the ROOT `package.json` "version", the file GitFlow already bumps
 * (`templates/skills/gitflow/cli/lib/version.ts` — root package.json is in its configured sources).
 * `Directory.Build.props` reads it into `<VersionPrefix>` with the same MSBuild regex the SmartStack
 * platform uses, so EVERY build (local, CI, Docker) stamps the assemblies. Without it a client's
 * assemblies default to 1.0.0 and the menu lies.
 *
 * Pure: no I/O. `init` and `upgrade` do the reading and writing.
 */

/**
 * Verbatim the platform's line. `Exists(...)` keeps a project WITHOUT a root package.json building
 * (assemblies fall back to 1.0.0) — deliberately no fail-loud target here: a client whose Dockerfile
 * was skipped as hand-modified would otherwise get a broken Docker build.
 */
export const VERSION_PREFIX_ELEMENT =
  `<VersionPrefix Condition="Exists('$(MSBuildThisFileDirectory)package.json')">` +
  `$([System.Text.RegularExpressions.Regex]::Match($([System.IO.File]::ReadAllText('$(MSBuildThisFileDirectory)package.json')), '"version"\\s*:\\s*"([^"]+)"').Groups[1].Value)` +
  `</VersionPrefix>`;

const VERSIONING_COMMENT =
  '<!-- Product version = the root package.json "version" (bumped by GitFlow). Reported by the API in the user menu. -->';

/** The first version a new project carries. */
export const INITIAL_CLIENT_VERSION = '0.1.0';

const SEMVER = /^\d+\.\d+\.\d+(?:[-+][0-9A-Za-z.-]+)?$/;

export type VersionPrefixStatus =
  | 'inserted'
  | 'already-versioned'
  | 'literal-version'
  | 'no-property-group';

/**
 * Inserts the `<VersionPrefix>` line as the first child of the first `<PropertyGroup>`, leaving the
 * rest of a (possibly customised) props file byte-identical.
 *
 * - a `<VersionPrefix` already there → untouched (`already-versioned`, idempotent);
 * - a literal `<Version>` → untouched (`literal-version`): the client pinned a number on purpose;
 * - no `<PropertyGroup>` → untouched (`no-property-group`).
 */
export function insertVersionPrefix(propsXml: string): { content: string; status: VersionPrefixStatus } {
  if (/<VersionPrefix\b/i.test(propsXml)) return { content: propsXml, status: 'already-versioned' };
  if (/<Version\b[^>]*>/i.test(propsXml)) return { content: propsXml, status: 'literal-version' };

  const group = /<PropertyGroup\b[^>]*>/i.exec(propsXml);
  if (!group) return { content: propsXml, status: 'no-property-group' };

  const newline = propsXml.includes('\r\n') ? '\r\n' : '\n';
  const after = propsXml.slice(group.index + group[0].length);
  const indent = /^\r?\n([ \t]+)/.exec(after)?.[1] ?? '    ';
  const insertion = `${newline}${indent}${VERSIONING_COMMENT}${newline}${indent}${VERSION_PREFIX_ELEMENT}`;

  return {
    content: propsXml.slice(0, group.index + group[0].length) + insertion + after,
    status: 'inserted',
  };
}

/** The `Directory.Build.props` a new project gets. */
export function buildClientDirectoryBuildProps(targetFramework: string): string {
  return `<Project>
  <PropertyGroup>
    ${VERSIONING_COMMENT}
    ${VERSION_PREFIX_ELEMENT}
    <TargetFramework>${targetFramework}</TargetFramework>
    <Nullable>enable</Nullable>
    <ImplicitUsings>enable</ImplicitUsings>
    <TreatWarningsAsErrors>false</TreatWarningsAsErrors>
  </PropertyGroup>
</Project>
`;
}

/** The root `package.json` — a version holder, not an npm project: private, no scripts, no deps. */
export function buildRootPackageJson(nameLower: string, version: string): string {
  return `${JSON.stringify({ name: nameLower, private: true, version }, null, 2)}\n`;
}

/**
 * The version an EXISTING project starts from when `ss upgrade` gives it a root package.json: what
 * GitFlow already believes (`.gitflow/config.json` versioning.current), else the latest release tag,
 * else {@link INITIAL_CLIENT_VERSION}. `0.0.0` is GitFlow's "unknown", never a real release.
 */
export function resolveSeedVersion(input: { gitflowCurrent?: string | null; latestTag?: string | null }): string {
  const current = input.gitflowCurrent?.trim();
  if (current && SEMVER.test(current) && current !== '0.0.0') return current;

  const tag = input.latestTag?.trim().replace(/^v/i, '');
  if (tag && SEMVER.test(tag) && tag !== '0.0.0') return tag;

  return INITIAL_CLIENT_VERSION;
}

/**
 * Adds a `"version"` to an existing root package.json that has none; anything else is left as is.
 * Returns `null` when nothing must change (version present, or the file is not a JSON object —
 * never rewrite what we cannot read).
 */
export function ensureVersionField(packageJsonText: string, version: string): string | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(packageJsonText);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const pkg = parsed as Record<string, unknown>;
  if (typeof pkg.version === 'string' && pkg.version.trim().length > 0) return null;

  const { name, ...rest } = pkg;
  const withVersion = name !== undefined ? { name, version, ...rest } : { version, ...rest };
  return `${JSON.stringify(withVersion, null, 2)}\n`;
}
